import { useRouter } from 'next/router';
import { useState } from 'react';
import { useCookies } from 'react-cookie';

import { useClient, useResignedTokens, useSessionStorage } from '../../hooks';
import { useNavigationWithQuery } from '../useNavigationWithQuery';

type RedirectHashParams = {
  access_token?: string;
  accessToken?: string;
  id_token?: string;
  state: string;
  refresh_token?: string;
  mfa?: string;
};

export function useRedirectToClient() {
  const { redirectURL, tenancy: TENANCY, clientState, clientId } = useClient();
  const { getSessionItem } = useSessionStorage();
  const { navigateTo } = useNavigationWithQuery();

  const router = useRouter();
  const resignedTokenCookieName = `IdentityAuthentication.${clientId}.token`;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cookies, setCookie] = useCookies([resignedTokenCookieName]);
  const { fetchResignedTokens } = useResignedTokens();
  const [redirectingToClient, setRedirectingToClient] = useState(false);

  const checkIfInviteSetup = () => {
    const inviteToken = getSessionItem('invite');
    if (inviteToken && clientState === inviteToken) return true;
    return false;
  };

  const redirectToClient = () => {
    if (checkIfInviteSetup()) {
      navigateTo({ path: 'inviteConfirmed' });
      return;
    }
    if (router.query && router.query.redirectTo) {
      const query = router.query;
      const { redirectTo } = query;
      delete query.redirectTo;
      router.push({ pathname: `/${redirectTo}`, query });
      return;
    }

    const resignedTokenCallback = (accessToken: string, idToken: string) => {
      if (!accessToken || !idToken) return;
      const hashParams: RedirectHashParams = {
        ...(TENANCY === 'APS'
          ? { access_token: accessToken, id_token: idToken }
          : { accessToken: idToken }),
        state: encodeURIComponent(clientState || ''),
      };

      window.location.replace(
        `${redirectURL}#?${(Object.keys(hashParams) as Array<
          keyof typeof hashParams
        >)
          .map((hashKey) => hashKey.concat('=', hashParams[hashKey]!))
          .join('&')}`
      );
    };
    setRedirectingToClient(true);
    fetchResignedTokens(resignedTokenCallback);
  };

  return { redirectToClient, redirectingToClient };
}
